<template>
  <div class="user-menu-wrap" @click.stop>
    <div
      class="user-info"
      role="button"
      tabindex="0"
      :data-open="String(visible)"
      :aria-label="displayName ? `${displayName}用户菜单` : '用户菜单'"
      @click="toggleVisible"
      @keydown.enter.prevent="toggleVisible"
      @keydown.space.prevent="toggleVisible"
    >
      <div class="user-avatar ws-avatar">{{ avatarChar }}</div>
      <span v-if="showName" class="user-name">{{ displayName }}</span>
      <icon-down :size="12" />
    </div>
    <div v-if="visible" class="user-dropdown-menu" role="menu">
      <div class="user-dropdown-profile" role="presentation">
        <span class="user-dropdown-avatar">{{ avatarChar }}</span>
        <span class="user-dropdown-name">{{ displayName || '管理员' }}</span>
      </div>
      <div class="user-dropdown-divider" />
      <button class="user-dropdown-item" type="button" role="menuitem" @click="onToggleTheme">
        <span class="user-dropdown-item-icon" aria-hidden="true">{{ themeIcon }}</span>
        {{ themeLabel }}
      </button>
      <div class="user-dropdown-divider" />
      <div class="user-dropdown-label">语言</div>
      <button
        class="user-dropdown-item"
        type="button"
        :data-active="String(currentLocale === 'zh-CN')"
        role="menuitemradio"
        :aria-checked="currentLocale === 'zh-CN'"
        @click="onChangeLocale('zh-CN')"
      >
        中文
      </button>
      <button
        class="user-dropdown-item"
        type="button"
        :data-active="String(currentLocale === 'en-US')"
        role="menuitemradio"
        :aria-checked="currentLocale === 'en-US'"
        @click="onChangeLocale('en-US')"
      >
        English
      </button>
      <div class="user-dropdown-divider" />
      <button class="user-dropdown-item user-dropdown-item--danger" type="button" role="menuitem" @click="handleLogout">
        退出登录
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { IconDown } from '@arco-design/web-vue/es/icon'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { currentLocale, setLocale, type SupportedLocale } from '@/i18n'

interface Props {
  /** 切换语言时的钩子。望舒工作台传入 useLanguage().setLanguage 以触发广播+持久化；workspace 不传，走默认 setLocale。 */
  setLanguage?: (locale: SupportedLocale) => void | Promise<void>
  /** 是否在触发器中显示用户名（true=workspace 风格，false=工作台紧凑模式）。默认 true。 */
  showName?: boolean
  /** 受控显示状态（可选）。不传则组件自管。 */
  open?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showName: true,
  open: undefined,
})

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'logout'): void
}>()

const authStore = useAuthStore()
const themeStore = useThemeStore()

const internalVisible = ref(false)
const visible = computed({
  get: () => (props.open === undefined ? internalVisible.value : props.open),
  set: (v: boolean) => {
    if (props.open === undefined) {
      internalVisible.value = v
    }
    emit('update:open', v)
  },
})

watch(
  () => props.open,
  (next) => {
    if (next !== undefined) internalVisible.value = next
  },
)

const displayName = computed(() => authStore.userName || '')
const avatarChar = computed(() => {
  const name = displayName.value
  return name ? name.charAt(0).toUpperCase() : '?'
})

const isDark = computed(
  () => themeStore.current === 'wangshu' || themeStore.current === 'dark',
)
const themeIcon = computed(() => (isDark.value ? '☀' : '☾'))
const themeLabel = computed(() => (isDark.value ? '切换为亮色' : '切换为暗色'))

function toggleVisible(): void {
  visible.value = !visible.value
}

function onToggleTheme(): void {
  if (themeStore.current === 'wangshu') {
    themeStore.setTheme('wangshu-light')
  } else if (themeStore.current === 'wangshu-light') {
    themeStore.setTheme('wangshu')
  } else {
    themeStore.toggleTheme()
  }
  visible.value = false
}

function onChangeLocale(locale: SupportedLocale): void {
  if (props.setLanguage) {
    void props.setLanguage(locale)
  } else {
    setLocale(locale)
  }
  visible.value = false
}

function handleLogout(): void {
  visible.value = false
  emit('logout')
  authStore.logout()
  // 与 MainLayout.handleLogout 保持一致：用 window.location 而非 router.push，
  // 绕过 Vue 调度器在 store 清空后可能的级联报错
  window.location.href = '/login'
}

function closeOnOutside(): void {
  if (visible.value) visible.value = false
}

onMounted(() => document.addEventListener('click', closeOnOutside))
onUnmounted(() => document.removeEventListener('click', closeOnOutside))
</script>

<style lang="scss" scoped>
.user-menu-wrap {
  position: relative;
  flex-shrink: 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px 4px 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--surface-3, var(--color-border-light));
  }
}

.user-info[data-open='true'] {
  background: var(--surface-2, var(--color-border-light));
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--c-text-1);
  color: var(--surface-1, var(--c-bg-surface));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  font-family: var(--c-font-sans);
}

.user-name {
  font-size: 13px;
  color: var(--text, var(--color-text-primary));
  font-weight: 500;
}

.user-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 200;
  min-width: 150px;
  padding: var(--space-1, 4px);
  color: var(--text, var(--color-text-primary));
  background: var(--surface-2, var(--color-bg-elevated));
  border: 1px solid var(--hairline, var(--color-border));
  border-radius: var(--radius-md, 10px);
  box-shadow: var(--shadow-2, 0 8px 24px rgba(0, 0, 0, 0.18));
}

.user-dropdown-profile {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  padding: 8px var(--space-3, 12px);
  color: var(--text, var(--color-text-primary));
  font-size: var(--fs-sm, 12px);
  font-weight: 600;
}

.user-dropdown-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--bg, var(--c-bg-canvas));
  background: var(--accent, var(--color-primary));
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
}

.user-dropdown-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-label {
  padding: 6px var(--space-3, 12px) 3px;
  color: var(--text-faint, var(--color-text-tertiary));
  font-size: var(--fs-xs, 11px);
  letter-spacing: 0.06em;
}

.user-dropdown-item {
  display: flex;
  width: 100%;
  align-items: center;
  gap: var(--space-2, 8px);
  padding: 7px var(--space-3, 12px);
  color: var(--text-muted, var(--color-text-secondary));
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm, 6px);
  font: inherit;
  font-size: var(--fs-sm, 12px);
  text-align: left;
  cursor: pointer;
}

.user-dropdown-item-icon {
  display: inline-flex;
  width: 14px;
  justify-content: center;
  color: currentColor;
  font-size: 12px;
  line-height: 1;
}

.user-dropdown-item:hover,
.user-dropdown-item[data-active='true'],
.user-dropdown-item[aria-checked='true'] {
  color: var(--text, var(--color-text-primary));
  background: var(--surface-3, var(--color-bg-hover));
}

.user-dropdown-item--danger {
  color: var(--danger, var(--color-error));
}

.user-dropdown-item--danger:hover {
  color: var(--danger, var(--color-error));
  background: var(--surface-3, var(--color-bg-hover));
}

.user-dropdown-divider {
  height: 1px;
  margin: 4px 0;
  background: var(--hairline, var(--color-border));
}
</style>
